"use client"
import { createContext, useContext, useState, type ReactNode } from "react"
import {
  useAiSpeedBreakdown,
  type AiSpeedBreakdown,
} from "../../../labor-mix/shared/useAiSpeedBreakdown"
import {
  HoverSwapFadeOverlay,
  hoverSwapHiddenContentStyle,
  useHoverSwapSequentialFade,
} from "../hoverSwapFade"
import {
  HUD_HOVER_RULE_CENTERED_TOP_PX,
  HUD_HOVER_RULE_TREATMENTS,
  HudHoverDescription,
  HudHoverDetailBody,
  HudHoverRuleTreatmentContext,
  HudHoverTitle,
} from "../hudHoverDetailHeader"
import { useHoverSwapsDisabled } from "../hoverSwapsDisabled"
import {
  CapabilityLadderRows,
  useCapabilityHoverSwap,
} from "../capability/capabilityHoverSwap"
import { useCurrentCapabilityStats } from "../capability/useCurrentCapabilityStats"

// ─── The labor-section hover (tabs 1–5) ──────────────────────────────────────
// Hovering the bottom (labor) section:
//   1. the section itself STAYS PUT and marks itself with the variant's
//      hover look (LaborHoverSectionHighlight — the five designs below), and
//   2. the ROW ABOVE (policies + distribution-of-power) is replaced in place
//      by the labor detail (LaborHoverRowTarget) — its footprint is kept, so
//      nothing on the canvas moves.
// The detail copy is PLACEHOLDER — made up to give the design something to
// say; edit freely.

// What the hovered section looks like — set per-variant via LaborHoverSwap's
// design prop (variants.tsx):
//   "frame"      — tab 1: a red rounded outline around the whole section
//   "rule-above" — tab 2: a red rule across the section's top edge
//   "left-bar"   — tab 3: a red bar down the section's left edge
//   "legend-red" — tab 4: the legend line's text and counts turn red
//   "title-tag"  — tab 5: a small red mono tag naming the section appears
//                  at its top-right corner
// The BOLD trio (tabs 6–8) — the metric-chip inversion vocabulary:
//   "banner"       — tab 6: tab 2's rule grows into a solid red band above
//                    the section with a white mono label
//   "legend-chips" — tab 7: each legend entry inverts onto its own red chip,
//                    white text/counts/swatches
//   "legend-plate" — tab 8: the whole legend line inverts onto one
//                    full-width red plate, white content
//   "dots-invert"  — tab 9: the policy squares' red-fill-invert applied to
//                    the DOTS: hollow human dots fill red, red AI dots turn
//                    to red outlines (legend swatches mirror the swap)
//   "rule-between" — tab 10: tab 2's rule, moved to sit midway in the gap
//                    BETWEEN the two sections instead of hugging the strip
export type LaborHoverDesignId =
  | "frame"
  | "rule-above"
  | "left-bar"
  | "legend-red"
  | "title-tag"
  | "banner"
  | "legend-chips"
  | "legend-plate"
  | "dots-invert"
  | "rule-between"

// ─── Editable content ────────────────────────────────────────────────────────
// Joanna's copy (2026-07-08), replacing the placeholder definitions.
export const LABOR_HOVER_COPY = {
  title: "Human vs. AI Labor",
  definition:
    "As the scenario progresses, more cognitive and manual labor is done by AI.",
  humanLabel: "Human Labor",
  humanDefinition:
    "Number of humans in the working population.",
  aiLabel: "AI Labor",
  aiDefinition:
    "Number of concurrent LLM thinking threads operating at any given moment in time, multiplied by their average human-relative thinking speed.",
}

// The detail that replaces the dist-of-power row: title + definition, then
// one full-width hanging-indent row per legend entry (swatch gutter, mono
// label run-in, sans sentence — the details panels' plan-row vocabulary).
// Sizes stay small (sans carries them); the calm comes from full-measure
// lines and real gaps instead of two packed side-by-side columns
// (2026-07-09). The row detail paints over a FIXED footprint that the old
// layout filled exactly on short viewports, so this restructure trades
// width for the added air at ~equal total height.
export const LABOR_HOVER_DETAIL_STYLE = {
  definitionLineHeight: 1.55,
  definitionMaxWidthEm: 46,
  // The human definition went one-line (2026-07-10), buying back the height
  // the speed-split line spent — the between-rows air widens toward (but
  // stays under) the body's 18px opener gap so the tiers read
  // title/definition › row › row.
  rowGapPx: 11,
  // Slightly tighter when the hover carries the bottom-right speed-split
  // line, so on short viewports (where the rows fill the footprint exactly)
  // the extra line stays clear of the labor section's hover rule below.
  rowGapWithSplitPx: 9,
  cardLabelFontSize: "10px",
  cardDefinitionFontSize: "9.5px",
  swatchDiameterPx: 7,
}

// The five section hover looks. All paint as overlays / box-shadows —
// nothing reflows when the hover lands or leaves.
export const LABOR_HOVER_SECTION_STYLE = {
  frame: { widthPx: 1.5, color: "var(--accent)", radiusPx: 4, insetPx: -5 },
  ruleAbove: { heightPx: 2, color: "var(--accent)", gapPx: 6 },
  leftBar: { widthPx: 2.5, color: "var(--accent)", gapPx: 7, radiusPx: 1 },
  legendRed: { color: "var(--accent)" },
  titleTag: {
    text: "LABOR MIX",
    fontSize: "8.5px",
    color: "var(--accent)",
    letterSpacing: "0.06em",
    offsetTopPx: -2,
  },
  // Tab 6 — the red band above the section (tab 2's rule, grown bold).
  banner: {
    text: "LABOR MIX",
    heightPx: 14,
    gapPx: 4,
    background: "var(--accent)",
    textColor: "#fff",
    fontSize: "8.5px",
    letterSpacing: "0.06em",
    paddingXPx: 6,
    radiusPx: 2,
  },
  // Tab 7 — per-entry chips (the tab-3/tab-0 metric-chip voice). Padding is
  // constant on this tab (side padding cancelled by negative margins so the
  // legend's outer edges stay flush); only the background/color toggle.
  legendChips: {
    background: "var(--accent)",
    textColor: "#fff",
    borderRadiusPx: 3,
    paddingXPx: 5,
    paddingYPx: 2,
  },
  // Tab 8 — the whole legend line on one full-width plate. Same
  // constant-padding rule as the chips.
  legendPlate: {
    background: "var(--accent)",
    textColor: "#fff",
    borderRadiusPx: 3,
    paddingXPx: 6,
    paddingYPx: 3,
  },
  // Tab 10 — tab 2's rule, sitting midway in the gap between the two
  // sections (the strip's slot has a 24px top margin; ~11px centers it).
  ruleBetween: {
    heightPx: 2,
    color: "var(--accent)",
    offsetPx: 11,
  },
}

// Tab 74: the labor hover's detail is the AGENT LEVEL TABLE (the capability
// ladder, current story-date level marked) instead of the human/AI labor
// definitions — the agent table explains what the AI half of the strip IS.
// Title/definition are DRAFT copy — edit freely.
export const LABOR_HOVER_AGENT_LADDER_COPY = {
  title: "AI Agent Levels",
  definition:
    "The rungs of AI capability — bold marks the level AI has reached at this point in the story.",
}

type LaborHoverSwapContextValue = {
  hovered: boolean
  setHovered: (hovered: boolean) => void
  design: LaborHoverDesignId
  // Tab 74: the row detail shows the agent-level table instead of the
  // human/AI labor definitions (LaborHoverAgentLadderDetail).
  agentLadder: boolean
}

const LaborHoverSwapContext = createContext<LaborHoverSwapContextValue | null>(null)

// Null outside a <LaborHoverSwap> (tabs without the treatment).
// BelowHudDetailSwapTarget reports enter/leave over the labor slot;
// LaborHoverRowTarget and LaborHoverSectionHighlight render the two halves.
export const useLaborHoverSwap = () => useContext(LaborHoverSwapContext)

// Renders no DOM of its own. Without a design the provider is a no-op
// (context stays null), so the body-maker can wrap unconditionally.
export const LaborHoverSwap = ({
  design,
  agentLadder = false,
  children,
}: {
  design?: LaborHoverDesignId
  agentLadder?: boolean
  children: ReactNode
}) => {
  const [hovered, setHovered] = useState(false)
  // Hover is not an input on this surface (mobile dashboard) — provide
  // null so every consumer renders its base, unhovered content.
  const hoverSwapsDisabled = useHoverSwapsDisabled()
  if (!design || hoverSwapsDisabled) return <>{children}</>
  return (
    <LaborHoverSwapContext.Provider value={{ hovered, setHovered, design, agentLadder }}>
      {children}
    </LaborHoverSwapContext.Provider>
  )
}

// The legend's swatch vocabulary: hollow ring = human, red fill = AI.
const LaborSwatch = ({ ai }: { ai: boolean }) => (
  <span
    className="inline-block shrink-0 rounded-full align-middle"
    style={{
      width: LABOR_HOVER_DETAIL_STYLE.swatchDiameterPx,
      height: LABOR_HOVER_DETAIL_STYLE.swatchDiameterPx,
      background: ai ? "var(--accent)" : "transparent",
      border: ai ? "none" : "1.25px solid #000",
    }}
  />
)

// The labor detail that paints over the policies/dist-of-power row.
export const LaborHoverRowDetail = () => {
  const style = LABOR_HOVER_DETAIL_STYLE
  const copy = LABOR_HOVER_COPY
  const speedBreakdown = useAiSpeedBreakdown()
  return (
    // h-full flex-col: the root fills the covered row's footprint (the
    // overlay in LaborHoverRowTarget spans it), so the speed-split line's
    // mt-auto pushes it to the hover's bottom-right corner — and on
    // viewports where the rows already fill the footprint it degrades to
    // sitting right under them instead of overlapping their text.
    <div className="flex h-full flex-col">
      <HudHoverTitle>{copy.title}</HudHoverTitle>
      <HudHoverDescription className="max-w-[46em]">
        {copy.definition}
      </HudHoverDescription>
      <HudHoverDetailBody
        className="grid"
        style={{
          marginTop: 8,
          rowGap: speedBreakdown ? style.rowGapWithSplitPx : style.rowGapPx,
        }}
      >
        {[
          { ai: false, label: copy.humanLabel, definition: copy.humanDefinition },
          { ai: true, label: copy.aiLabel, definition: copy.aiDefinition },
        ].map((entry) => (
          // Hanging indent: the swatch gutters left, the label runs into the
          // sentence so wrapped lines indent under the text.
          <div
            key={entry.label}
            className="grid grid-cols-[18px_1fr]"
            style={{ maxWidth: `${style.definitionMaxWidthEm}em` }}
          >
            {/* Sized to the label's first line box and flex-centered, so the
                dot sits on the row's first line regardless of the inherited
                font's line box. */}
            <span
              className="flex items-center justify-self-start"
              aria-hidden
              style={{
                height: `calc(${style.cardLabelFontSize} * ${style.definitionLineHeight})`,
              }}
            >
              <LaborSwatch ai={entry.ai} />
            </span>
            <span
              className="font-sans"
              style={{
                fontSize: style.cardDefinitionFontSize,
                lineHeight: style.definitionLineHeight,
              }}
            >
              <b
                className="mr-1.5 font-mono font-bold"
                style={{ fontSize: style.cardLabelFontSize }}
              >
                {entry.label}
              </b>
              {entry.definition}
            </span>
          </div>
        ))}
      </HudHoverDetailBody>
      {speedBreakdown && <AiSpeedSplitLine breakdown={speedBreakdown} />}
    </div>
  )
}

// The counts render in the legend's number style (2 significant digits,
// "28M" / "23K") so the split visibly sums to the legend's agent count.
const SPEED_SPLIT_COUNT_FORMAT = new Intl.NumberFormat("en-US", {
  notation: "compact",
  minimumSignificantDigits: 2,
  maximumSignificantDigits: 2,
})

// The AI row's live speed split, pinned to the hover's bottom-right corner —
// how many of the fleet run at which speed, at the HUD's animated date: the
// bulk on general hardware at the typical speed, plus the small
// inference-specialized fleet running at the legend's "up to" speed. It
// reads as an equation ending in the fleet TOTAL, rendered in the legend's
// number voice (bold, red, 11px — LegendCount in LaborDotStripLeaderRows)
// so it visibly sums to the agent count displayed in the legend below. Only
// rendered when the split is defined (not on branch plans; not post-2040,
// where the sheet retires it).
const AiSpeedSplitLine = ({ breakdown }: { breakdown: AiSpeedBreakdown }) => (
  <span
    className="mt-auto block self-end whitespace-nowrap pt-0.5 text-right font-mono leading-none"
    style={{ fontSize: LABOR_HOVER_DETAIL_STYLE.cardDefinitionFontSize }}
  >
    {SPEED_SPLIT_COUNT_FORMAT.format(breakdown.countGeneral)} at ×{breakdown.generalMultiplier} speed
    {" + "}
    {SPEED_SPLIT_COUNT_FORMAT.format(breakdown.countSpecialized)} at ×{breakdown.specializedMultiplier} speed
    {" = "}
    <b className="text-[11px] text-[var(--accent)]">
      {SPEED_SPLIT_COUNT_FORMAT.format(breakdown.countGeneral + breakdown.countSpecialized)}
    </b>
  </span>
)

// Tab 74's row detail: the agent-level table — the capability ladder with
// the STORY DATE's level marked (useCurrentCapabilityStats interpolates it
// from the graph's own data), in the tab's ladder treatment (read from the
// capability swap provider so this table and the details page match).
export const LaborHoverAgentLadderDetail = () => {
  const copy = LABOR_HOVER_AGENT_LADDER_COPY
  const stats = useCurrentCapabilityStats()
  const capabilitySwap = useCapabilityHoverSwap()
  return (
    <div>
      <HudHoverTitle>{copy.title}</HudHoverTitle>
      <HudHoverDescription className="max-w-[46em]">
        {copy.definition}
      </HudHoverDescription>
      <HudHoverDetailBody>
        <CapabilityLadderRows
          currentLabel={stats?.levelLabel ?? null}
          highlightDisabled={capabilitySwap?.ladderRowHighlightDisabled}
          highlight={capabilitySwap?.ladderHighlight}
          descriptionLayout={capabilitySwap?.ladderDescriptionLayout}
        />
      </HudHoverDetailBody>
    </div>
  )
}

// Wraps the policies/dist-of-power ROW. While the labor section below is
// hovered, the row's children keep their layout footprint but fade out, and
// the labor detail paints over them — nothing on the canvas moves.
export const LaborHoverRowTarget = ({ children }: { children: ReactNode }) => {
  const swap = useLaborHoverSwap()
  const sequentialFade = useHoverSwapSequentialFade()
  const active = Boolean(swap?.hovered)
  if (!swap) return <>{children}</>
  return (
    <div className="relative">
      <div style={hoverSwapHiddenContentStyle(active, sequentialFade)}>
        {children}
      </div>
      {/* pointer-events-none: the (invisible) row stays hit-testable, so
          moving the pointer up into it hands hover back to the row's own
          families in one beat. bottom-0 (unlike the other swap overlays):
          the detail spans the covered row's full footprint so its
          speed-split line can pin to the hover's bottom-right corner. */}
      <HoverSwapFadeOverlay
        active={active}
        contentKey="labor"
        className="absolute inset-0 z-10 pointer-events-none"
      >
        {swap.agentLadder ? <LaborHoverAgentLadderDetail /> : <LaborHoverRowDetail />}
      </HoverSwapFadeOverlay>
    </div>
  )
}

// Wraps the labor section itself: applies the design's hover look. Every
// treatment is an overlay or box-shadow (constant keys, toggled values), so
// the section never shifts — it just gets marked.
export const LaborHoverSectionHighlight = ({
  children,
}: {
  children: ReactNode
}) => {
  const swap = useLaborHoverSwap()
  // The "rule-above" rule follows the tab's hover-rule treatment (see
  // HUD_HOVER_RULE_TREATMENTS in hudHoverDetailHeader.tsx) so the labor rule
  // and the detail rules stay one system; "stock" keeps this file's own
  // LABOR_HOVER_SECTION_STYLE.ruleAbove knobs.
  const ruleTreatmentId = useContext(HudHoverRuleTreatmentContext)
  if (!swap) return <>{children}</>
  const { hovered, design } = swap
  const style = LABOR_HOVER_SECTION_STYLE
  const ruleAbove =
    ruleTreatmentId === "stock"
      ? { heightPx: style.ruleAbove.heightPx, background: style.ruleAbove.color }
      : HUD_HOVER_RULE_TREATMENTS[ruleTreatmentId]
  return (
    <div
      className="relative"
      style={{
        // "legend-red": text without an explicit color (labels, speeds)
        // inherits; LaborDotStripLeaderRows reds its counts via the same
        // context.
        color:
          design === "legend-red" && hovered ? style.legendRed.color : undefined,
      }}
    >
      {design === "frame" && (
        <span
          aria-hidden
          className="absolute pointer-events-none"
          style={{
            inset: style.frame.insetPx,
            borderRadius: style.frame.radiusPx,
            boxShadow: hovered
              ? `0 0 0 ${style.frame.widthPx}px ${style.frame.color}`
              : "0 0 0 0 rgba(0,0,0,0)",
          }}
        />
      )}
      {design === "rule-above" && hovered && (
        <span
          aria-hidden
          className="absolute left-0 right-0 pointer-events-none"
          style={{
            // Non-stock treatments hang the rule at the shared centered
            // offset — the exact spot the below-HUD detail rule occupies, so
            // the line never jumps between labor hover and detail hovers.
            top:
              ruleTreatmentId === "stock"
                ? -(style.ruleAbove.gapPx + ruleAbove.heightPx)
                : HUD_HOVER_RULE_CENTERED_TOP_PX,
            height: ruleAbove.heightPx,
            background: ruleAbove.background,
          }}
        />
      )}
      {design === "rule-between" && hovered && (
        <span
          aria-hidden
          className="absolute left-0 right-0 pointer-events-none"
          style={{
            top: -(style.ruleBetween.offsetPx + style.ruleBetween.heightPx),
            height: style.ruleBetween.heightPx,
            background: style.ruleBetween.color,
          }}
        />
      )}
      {design === "left-bar" && hovered && (
        <span
          aria-hidden
          className="absolute top-0 bottom-0 pointer-events-none"
          style={{
            left: -(style.leftBar.gapPx + style.leftBar.widthPx),
            width: style.leftBar.widthPx,
            borderRadius: style.leftBar.radiusPx,
            background: style.leftBar.color,
          }}
        />
      )}
      {design === "title-tag" && hovered && (
        <span
          aria-hidden
          className="absolute right-0 font-mono font-bold pointer-events-none"
          style={{
            bottom: `calc(100% + ${style.titleTag.offsetTopPx}px)`,
            fontSize: style.titleTag.fontSize,
            letterSpacing: style.titleTag.letterSpacing,
            color: style.titleTag.color,
          }}
        >
          {style.titleTag.text}
        </span>
      )}
      {design === "banner" && hovered && (
        <span
          aria-hidden
          className="absolute left-0 right-0 flex items-center font-mono font-bold pointer-events-none"
          style={{
            bottom: `calc(100% + ${style.banner.gapPx}px)`,
            height: style.banner.heightPx,
            background: style.banner.background,
            color: style.banner.textColor,
            fontSize: style.banner.fontSize,
            letterSpacing: style.banner.letterSpacing,
            paddingLeft: style.banner.paddingXPx,
            paddingRight: style.banner.paddingXPx,
            borderRadius: style.banner.radiusPx,
          }}
        >
          {style.banner.text}
        </span>
      )}
      {children}
    </div>
  )
}

// Which legend inversion the strip's legend should render (null = none) —
// read by LaborDotStripLeaderRows, which owns the legend markup.
export const useLaborLegendTreatment = ():
  | "red"
  | "chips"
  | "plate"
  | null => {
  const swap = useLaborHoverSwap()
  if (!swap?.hovered) return null
  if (swap.design === "legend-red") return "red"
  if (swap.design === "legend-chips") return "chips"
  if (swap.design === "legend-plate") return "plate"
  return null
}

// Whether the current tab's design is one of the legend inversions — the
// legend keeps the (constant) chip/plate padding while idle so nothing
// shifts when the hover lands.
export const useLaborLegendPaddingReserved = ():
  | "chips"
  | "plate"
  | null => {
  const swap = useLaborHoverSwap()
  if (swap?.design === "legend-chips") return "chips"
  if (swap?.design === "legend-plate") return "plate"
  return null
}

// Tab 9 "dots-invert": whether the strip's dots (and legend swatches) are
// currently role-swapped — hollow human dots filled red, red AI dots turned
// to red outlines. Read by LaborDotStripLeaderRows, which owns the canvas
// props and the legend markup.
export const useLaborDotsInverted = (): boolean => {
  const swap = useLaborHoverSwap()
  return swap?.design === "dots-invert" && swap.hovered
}
